// Theme registry for the public event pages (#160243).
//
// A theme is a LAYOUT (which .vue files render each public route — see
// ./views.js) plus a stylesheet. `style_theme` from GET /cms/header-config
// picks it; the four legacy names keep the legacy layout and only change
// CSS, exactly as Events.vue has always done.
import customerService from '@/services/plugins/customer';

export const LEGACY = 'legacy';
export const NEW_ERA = 'new_era'; // Dacian's name for the design, 2026-10-07
export const DEFAULT_STYLE_THEME = 'beta';

const LEGACY_STYLE_THEMES = ['beta', 'delta', 'valentino', 'custom-events'];

// The layout comes from the backend only: the merchant's `style_theme` in
// GET /cms/header-config (Event Configuration → Style theme). There is no
// URL or browser-storage switch.
export function resolveTheme(styleTheme) {
  if (styleTheme === NEW_ERA) {
    return { layout: NEW_ERA, styleTheme: NEW_ERA };
  }
  const css = LEGACY_STYLE_THEMES.includes(styleTheme)
    ? styleTheme
    : DEFAULT_STYLE_THEME;
  return { layout: LEGACY, styleTheme: css };
}

export async function loadThemeCss(theme) {
  if (theme.layout === NEW_ERA) {
    await Promise.all([
      import(/* webpackChunkName: "new-era" */ '@/themes/new_era/index.scss'),
      // html/body reset; kept in its own file so it is not id-prefixed
      import(
        /* webpackChunkName: "new-era" */ '@/themes/new_era/excludedStyles.scss'
      ),
    ]);
    return;
  }
  try {
    await import('@/assets/servers/' + theme.styleTheme + '/index.css');
  } catch (err) {
    console.log(
      `Unknown style_theme "${theme.styleTheme}", falling back: `,
      err,
    );
    await import('@/assets/servers/' + DEFAULT_STYLE_THEME + '/index.css');
  }
}

let headerConfigPromise = null;
export function getHeaderConfigOnce() {
  if (!headerConfigPromise) {
    headerConfigPromise = customerService
      .getEventsHeaderConfig()
      .then((res) => res.data)
      .catch((err) => {
        headerConfigPromise = null; // let the next caller retry
        throw err;
      });
  }
  return headerConfigPromise;
}

// A request that never answers must not leave the page blank.
export const HEADER_CONFIG_TIMEOUT_MS = 8000;
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('header-config timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

let themePromise = null;
// Resolves the theme ONCE per page load and never rejects. When the header
// config or the theme stylesheet cannot be loaded it answers with the legacy
// layout and keeps that answer: vue-router resolves each lazy route component
// only once, so a different answer later would put a new_era view inside the
// legacy shell. A reload starts over.
export function currentTheme({
  load = loadThemeCss,
  timeoutMs = HEADER_CONFIG_TIMEOUT_MS,
} = {}) {
  if (!themePromise) {
    themePromise = (async () => {
      let styleTheme = '';
      let known = true;
      try {
        styleTheme = (await withTimeout(getHeaderConfigOnce(), timeoutMs))
          .style_theme;
      } catch (err) {
        known = false;
        console.log('Error while fetching header config: ', err);
      }
      const theme = resolveTheme(styleTheme);
      // Without the merchant's style_theme the legacy skin is not known here.
      // Leave it to the legacy shell, which loads it from its own request;
      // loading the default one as well would stack two skins.
      if (!known && theme.layout === LEGACY) return theme;
      try {
        await load(theme);
        return theme;
      } catch (err) {
        console.log('Theme stylesheet failed to load: ', err);
        return resolveTheme('');
      }
    })();
  }
  return themePromise;
}

export function _resetThemeCache() {
  headerConfigPromise = null;
  themePromise = null;
}
